import { Field } from "@/types/field";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import {
    Button,
    Checkbox,
    CheckboxGroup,
    DateValue,
    Divider,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerHeader,
    RangeValue,
    Selection,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { parseDate } from "@internationalized/date";
import dayjs from "dayjs";
import DateRangePickerWithPresets from "@/components/inputs/DateRangePickerWithPresets";
import SITrendline from "@/components/field/insights/SITrendline";
import StatusTimeline from "@/components/home/StatusTimeline";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import useRequest from "@/lib/request/useRequest";
import { TrendlineTable } from "@/types/plot";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { useTranslation } from "react-i18next";

function PreviewDrawer({
    drawerOpen,
    onDrawerOpen,
    onDrawerClose,
    setSelectedKeys,
    selectedField,
}: {
    drawerOpen: boolean;
    onDrawerOpen: () => void;
    onDrawerClose: () => void;
    setSelectedKeys: (keys: Selection) => void;
    selectedField: Field;
}) {
    const { t } = useTranslation("home-field");
    const { t: tInsights } = useTranslation("insights");
    const { notificationsV2Enabled } = useFeatureFlag();
    const { useGetRequest } = useRequest();

    const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>({
        start: parseDate(dayjs().subtract(2, "weeks").format("YYYY-MM-DD")),
        end: parseDate(dayjs().format("YYYY-MM-DD")),
    });
    const [selectedPairIds, setSelectedPairIds] = useState<string[]>([
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "10",
    ]);

    const tableQuery = useGetRequest<TrendlineTable[]>({
        url: `fields/${selectedField.id}/trendline_table/`,
        enabled: drawerOpen && !!selectedField?.id,
        params: {
            values: "si",
            groupby: "block",
        },
    });

    const pairIdOptions = useMemo(() => {
        return sortArrayByKey(
            [...new Set(tableQuery?.data?.map((row) => row.pair_id) ?? [])].map(
                (pair) => ({ id: pair, description: pair }),
            ),
            "id",
        );
    }, [tableQuery?.data]);

    return (
        <Drawer
            isOpen={drawerOpen}
            onOpenChange={onDrawerOpen}
            onClose={() => {
                onDrawerClose();
                setSelectedKeys(new Set([]));
            }}
            size="3xl"
            scrollBehavior="inside"
        >
            <DrawerContent>
                <DrawerHeader>
                    <div className="flex flex-col gap-2 items-start">
                        <div className="font-semibold">
                            {selectedField.name}
                        </div>
                        <div className="text-xs ">{selectedField.grower}</div>
                    </div>
                </DrawerHeader>
                <DrawerBody>
                    <div>
                        <Button
                            fullWidth
                            as={Link}
                            to={`/dashboard/fields/$fieldId/insights/ntime/?cameFrom=home`}
                            params={
                                {
                                    fieldId: selectedField.id.toString(),
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                } as any
                            }
                            search={{
                                // @ts-expect-error - Button -> Link
                                advisor: undefined,
                            }}
                            startContent={
                                <FontAwesomeIcon
                                    icon={faArrowUpRightFromSquare}
                                />
                            }
                            color="primary"
                        >
                            {t("go_to_field_dashboard")}
                        </Button>
                    </div>

                    <div className="flex flex-col gap-2 mx-1">
                        <DateRangePickerWithPresets
                            value={dateRange}
                            onChange={setDateRange}
                        />
                        {pairIdOptions.length > 0 && (
                            <div className="flex flex-col">
                                <div className="font-semibold text-sm">
                                    {tInsights("plotPairs", {
                                        ns: "insights",
                                    })}
                                </div>
                                <CheckboxGroup
                                    orientation="horizontal"
                                    value={selectedPairIds}
                                    onValueChange={setSelectedPairIds}
                                    size="sm"
                                >
                                    {pairIdOptions.map((option) => (
                                        <Checkbox
                                            key={option.id}
                                            value={option.id.toString()}
                                        >
                                            {option.description}
                                        </Checkbox>
                                    ))}
                                </CheckboxGroup>
                            </div>
                        )}
                        <SITrendline
                            fieldId={selectedField.id.toString()}
                            disableResize
                            tableQuery={tableQuery}
                            dateRange={dateRange}
                            selectedPairIds={selectedPairIds}
                            cropStage={undefined}
                        />
                    </div>
                    <Divider />
                    {notificationsV2Enabled && (
                        <StatusTimeline fieldId={selectedField.id} />
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}

export default PreviewDrawer;
